import { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { getGenerations, getOverview, AdminGenerationRow, generationStatusPillClass, formatMicroUsd } from '../../lib/adminApi';
import { formatDate } from '../../lib/date';
import GenerationPreviewModal from './GenerationPreviewModal';

function fmtShortDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const PAGE_SIZE = 20;

function AdminGenerations() {
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [minCost, setMinCost] = useState('');
  const [maxCost, setMaxCost] = useState('');
  const [sort, setSort] = useState('newest');

  const [generations, setGenerations] = useState<AdminGenerationRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  // Trend data from overview
  const [trendData, setTrendData] = useState<Array<{ date: string; total: number }>>([]);

  const [previewId, setPreviewId] = useState<string | null>(null);

  // Fetch 30-day trend from overview
  useEffect(() => {
    const trendTo = new Date();
    const trendFrom = new Date();
    trendFrom.setDate(trendFrom.getDate() - 30);
    getOverview(
      trendFrom.toISOString().slice(0, 10),
      trendTo.toISOString().slice(0, 10),
    ).then(data => {
      const mapped = data.generationsByDay.map(d => ({
        date: fmtShortDate(d.date),
        total: d.succeeded + d.failed,
      }));
      setTrendData(mapped);
    }).catch(() => {});
  }, []);

  const fetchGenerations = useCallback(async (cursor?: string) => {
    const isLoadMore = !!cursor;
    if (isLoadMore) setLoadingMore(true);
    else {
      setLoading(true);
      setGenerations([]);
    }
    setError('');
    try {
      const result = await getGenerations({
        status: status || undefined,
        provider: provider || undefined,
        model: model || undefined,
        search: search || undefined,
        from: from || undefined,
        to: to || undefined,
        minCost: minCost || undefined,
        maxCost: maxCost || undefined,
        sort: sort || undefined,
        limit: PAGE_SIZE,
        cursor: cursor || undefined,
      });
      if (isLoadMore) {
        setGenerations(prev => [...prev, ...result.generations]);
      } else {
        setGenerations(result.generations);
      }
      setNextCursor(result.nextCursor);
    } catch {
      setError('Failed to load generations.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [status, provider, model, search, from, to, minCost, maxCost, sort]);

  useEffect(() => {
    fetchGenerations();
  }, [fetchGenerations]);

  return (
    <div>
      <div className="admin-topbar">
        <h1>Generations</h1>
      </div>
      <div className="admin-content">
        {/* 30-day trend sparkline */}
        {trendData.length > 0 && (
          <div className="admin-chart-card" style={{ marginBottom: '1.5rem' }}>
            <h3>Generations — Last 30 Days</h3>
            <p>Total (succeeded + failed) per day</p>
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={trendData}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#C87C34"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Filter bar */}
        <div className="admin-filter-bar">
          <div className="admin-filter-group">
            <label className="admin-filter-label">Status</label>
            <select
              className="admin-filter-select"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="COMPLETED">Succeeded</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Provider</label>
            <select
              className="admin-filter-select"
              value={provider}
              onChange={e => setProvider(e.target.value)}
            >
              <option value="">All</option>
              <option value="gemini">Gemini</option>
              <option value="stability">Stability AI</option>
              <option value="openai">OpenAI</option>
            </select>
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Model</label>
            <input
              className="admin-filter-input"
              placeholder="e.g. gemini-pro"
              value={model}
              onChange={e => setModel(e.target.value)}
              style={{ width: 160 }}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">User Search</label>
            <input
              className="admin-filter-input"
              placeholder="Email or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 200 }}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">From</label>
            <input
              type="date"
              className="admin-filter-input"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">To</label>
            <input
              type="date"
              className="admin-filter-input"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Min Cost (μ$)</label>
            <input
              className="admin-filter-input"
              placeholder="0"
              value={minCost}
              onChange={e => setMinCost(e.target.value)}
              style={{ width: 90 }}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Max Cost (μ$)</label>
            <input
              className="admin-filter-input"
              placeholder="∞"
              value={maxCost}
              onChange={e => setMaxCost(e.target.value)}
              style={{ width: 90 }}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Sort</label>
            <select
              className="admin-filter-select"
              value={sort}
              onChange={e => setSort(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="cost_desc">Cost ↓</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" role="alert">{error}</div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Status</th>
                    <th>Provider</th>
                    <th>Date</th>
                    <th>Tokens</th>
                    <th>Cost (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {generations.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="admin-table-empty">No generations found.</td>
                    </tr>
                  ) : (
                    generations.map(g => (
                      <tr key={g.id} onClick={() => setPreviewId(g.id)}>
                        <td>
                          <div>{g.userEmail}</div>
                        </td>
                        <td>
                          <span className={`admin-pill admin-pill-${generationStatusPillClass(g.status)}`}>
                            {g.status}
                          </span>
                        </td>
                        <td>{g.aiProvider}</td>
                        <td>{formatDate(g.createdAt)}</td>
                        <td>{g.totalTokens}</td>
                        <td>{formatMicroUsd(g.costMicroUsd)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {nextCursor && (
              <div className="admin-load-more">
                <button
                  className="btn-secondary"
                  onClick={() => fetchGenerations(nextCursor)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {previewId && (
        <GenerationPreviewModal
          generationId={previewId}
          onClose={() => setPreviewId(null)}
        />
      )}
    </div>
  );
}

export default AdminGenerations;
